import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { TEMPLATE_SCOPE_TYPES } from '@experiment-management-shared/constants/chartsGallery';
import { format } from 'date-fns';

import { Table } from '@DesignSystem/tables';
import { BuiltInGallery } from '../BuiltInGallery';

import TableEmptyState from '@shared/components/TableEmptyState';
import { DSColors } from '@design-system-outdated/constants';
import { GalleryTableActionsCell } from '@experiment-management-shared/components/PanelModal/GalleryTable/components/GalleryTableActionsCell';
import { TextButton } from '@ds';
import { useHistory, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { BUILT_IN_CHART_TYPES } from '@experiment-management-shared/constants';
import dashboardChartsActions from '@/actions/dashboardChartsActions';
import { PythonEmptyStateIcon } from '@experiment-management-shared/components/AddBuiltInVisualizationModal/subcomponents/icons';

const NAME_TITLE = 'Name';
const CREATED_BY_TITLE = 'Created By';
const LAST_UPDATED = 'Last updated';
const DESCRIPTION = 'Description';
const NO_NAME = ' ';

export const CHART_TABLE_COLUMNS_NAMES = {
  NAME: 'name',
  CREATED_BY_TITLE: 'createdBy',
  LAST_UPDATED: 'lastUpdated',
  DESCRIPTION: 'description',
  ACTIONS: 'actions',
  ADD_BTN: 'addBtn'
};

export const CHART_TABLE_COLUMNS = [
  { name: CHART_TABLE_COLUMNS_NAMES.NAME, title: NAME_TITLE },
  { name: CHART_TABLE_COLUMNS_NAMES.CREATED_BY_TITLE, title: CREATED_BY_TITLE },
  { name: CHART_TABLE_COLUMNS_NAMES.LAST_UPDATED, title: LAST_UPDATED },
  { name: CHART_TABLE_COLUMNS_NAMES.DESCRIPTION, title: DESCRIPTION },
  { name: CHART_TABLE_COLUMNS_NAMES.ADD_BTN, title: NO_NAME },
  { name: CHART_TABLE_COLUMNS_NAMES.ACTIONS, title: NO_NAME }
];

export const ASSET_TABLE_COLUMNS_WIDTH = [
  {
    columnName: CHART_TABLE_COLUMNS_NAMES.ACTIONS,
    width: 45,
    resizingEnabled: false
  },
  {
    columnName: CHART_TABLE_COLUMNS_NAMES.NAME,
    width: 300,
    resizingEnabled: false
  },
  {
    columnName: CHART_TABLE_COLUMNS_NAMES.ADD_BTN,
    width: 170,
    resizingEnabled: false
  }
];

const GalleryTable = ({ activeTab, chartTemplates, isSearchText }) => {
  const isBuiltIn = activeTab === TEMPLATE_SCOPE_TYPES.BUILT_IN;
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [sorting, setSorting] = React.useState([
    { columnName: CHART_TABLE_COLUMNS_NAMES.LAST_UPDATED, direction: 'desc' }
  ]);

  if (isBuiltIn) {
    return <BuiltInGallery charts={chartTemplates} />;
  }

  const dataTypes = [
    {
      cols: [CHART_TABLE_COLUMNS_NAMES.ACTIONS],
      cell: GalleryTableActionsCell
    },
    {
      cols: [CHART_TABLE_COLUMNS_NAMES.ADD_BTN],
      cell: ({ row }) => {
        const { id, description, name } = row;
        const handleOpenPreview = () => {
          dispatch(
            dashboardChartsActions.updateChartForm(
              BUILT_IN_CHART_TYPES.libraryPanel,
              {
                chartName: name,
                description,
                codeVersion: '',
                code: null,
                // instanceName is always equal to chartName. Added for backward compatibility with old viz panels
                instanceName: name
              }
            )
          );

          // Currently, templateId is stored in the URL to maintain backward compatibility with existing logic.
          // In the future, we may move templateId to Redux for improved state management.
          const templatePreviewParams = `&chartType=${BUILT_IN_CHART_TYPES.libraryPanel}&templateId=${id}&isPreview=true`;

          history.push({
            ...location,
            hash: location.hash + templatePreviewParams
          });
        };

        return (
          <TextButton onClick={handleOpenPreview}>Add to dashboard</TextButton>
        );
      }
    },
    {
      cols: [CHART_TABLE_COLUMNS_NAMES.LAST_UPDATED],
      cell: ({ row }) => {
        return format(new Date(row.lastUpdated), 'MM/dd/yy hh:mm a');
      }
    }
  ];

  const rows = chartTemplates.map(chartTemplate => ({
    ...chartTemplate,
    [CHART_TABLE_COLUMNS_NAMES.NAME]: chartTemplate.name,
    [CHART_TABLE_COLUMNS_NAMES.CREATED_BY_TITLE]: chartTemplate.owner,
    [CHART_TABLE_COLUMNS_NAMES.LAST_UPDATED]: chartTemplate.lastSaved,
    [CHART_TABLE_COLUMNS_NAMES.DESCRIPTION]: chartTemplate?.description || ''
  }));

  const renderEmptyState = props => {
    return isSearchText ? (
      <TableEmptyState
        label="We couldn't find any panels for your search"
        materialProps={props}
        picturePath={'/images/empty-states/no-search-results.svg'}
      />
    ) : (
      <TableEmptyState
        label="No panels saved"
        materialProps={props}
        pictureWidth={0}
        pictureHeight={0}
        icon={<PythonEmptyStateIcon />}
      />
    );
  };

  return (
    <div className={cx('charts-gallery-table-container', activeTab)}>
      <Table
        dataTypes={dataTypes}
        columns={CHART_TABLE_COLUMNS}
        rows={rows}
        className="chart-gallery-table"
        rowHeight={60}
        rowIdKey="id"
        maxHeight={380}
        renderEmptyState={renderEmptyState}
        header={{
          headerColor: DSColors.grayColor1
        }}
        columnOrderConfig={{
          isDisabled: true
        }}
        sortingConfig={{
          columnSorting: sorting,
          onSortingChange: setSorting,
          isDisabled: false
        }}
        columnWidthsConfig={{
          isDisabled: true,
          columnExtensions: ASSET_TABLE_COLUMNS_WIDTH
        }}
        paginationConfig={{
          isDisabled: true
        }}
        selectionConfig={{
          isDisabled: true
        }}
      />
    </div>
  );
};

GalleryTable.defaultProps = {
  chartTemplates: []
};

GalleryTable.propTypes = {
  activeTab: PropTypes.string.isRequired,
  chartTemplates: PropTypes.array,
  onChangeTemplate: PropTypes.func.isRequired,
  isSearchText: PropTypes.bool
};

export default GalleryTable;
